import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { MessageSquare, Clock, CheckCircle, XCircle, Plus, Bell, Play } from 'lucide-react'
import { api } from '../utils/api'
import { format } from 'date-fns'

export default function Dashboard() {
  const queryClient = useQueryClient()
  const [now, setNow] = useState(() => new Date())
  
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date())
    }, 1000)
    return () => clearInterval(interval)
  }, [])
  
  const { data: analytics } = useQuery({
    queryKey: ['analytics'],
    queryFn: api.analytics,
  })
  
  const { data: beatMessages } = useQuery({
    queryKey: ['beatMessages'],
    queryFn: api.messages.beat.pending,
  })
  
  const { data: recentMessages } = useQuery({
    queryKey: ['recentMessages'],
    queryFn: () => api.messages.list({ status: 'pending' }),
  })

  const punchMutation = useMutation({
    mutationFn: () => api.messages.beat.punchAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['beatMessages'] })
    },
  })

  const punchSingleMutation = useMutation({
    mutationFn: (id: number) => api.messages.punch(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['beatMessages'] })
    },
  })

  function getTimeRemaining(msg: any, now: Date): { text: string; isUrgent: boolean; isSending: boolean } {
    const lastPunch = msg.beat_last_punch ? new Date(msg.beat_last_punch + 'Z') : new Date(msg.created_at + 'Z')
    const intervalMs = msg.beat_interval_hours * 60 * 60 * 1000
    const expiresAt = new Date(lastPunch.getTime() + intervalMs)
    const diff = expiresAt.getTime() - now.getTime()
    
    if (diff <= 0) {
      return { text: 'SENDING SOON', isUrgent: true, isSending: true }
    }
    
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((diff % (1000 * 60)) / 1000)
    
    const isUrgent = hours === 0 && minutes < 30
    
    if (hours > 0) {
      return { text: `${hours}h ${minutes}m ${seconds}s`, isUrgent, isSending: false }
    } else if (minutes > 0) {
      return { text: `${minutes}m ${seconds}s`, isUrgent, isSending: false }
    } else {
      return { text: `${seconds}s`, isUrgent: true, isSending: false }
    }
  }
  
  const stats = [
    { label: 'Total Messages', value: analytics?.total || 0, icon: MessageSquare, color: 'blue' },
    { label: 'Sent', value: analytics?.sent || 0, icon: CheckCircle, color: 'green' },
    { label: 'Pending', value: analytics?.pending || 0, icon: Clock, color: 'yellow' },
    { label: 'Failed', value: analytics?.failed || 0, icon: XCircle, color: 'red' },
  ]
  
  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <Link to="/messages/new" className="btn btn-primary flex items-center gap-2">
          <Plus className="w-5 h-5" />
          New Message
        </Link>
      </div>
      
      {beatMessages && beatMessages.length > 0 && (
        <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl p-6 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <Bell className="w-6 h-6" />
            <h2 className="text-xl font-bold">Beat Messages - Punch to Reset Timer</h2>
          </div>
          
          <div className="space-y-2 mb-4">
            {beatMessages.map((msg: any) => {
              const { text: timeText, isUrgent, isSending } = getTimeRemaining(msg, now)
              return (
                <div key={msg.id} className="bg-white/20 rounded-lg p-3 flex justify-between items-center">
                  <div className="truncate max-w-xs">
                    <p className="font-medium">{msg.subject || msg.body?.substring(0, 50)}</p>
                    <p className="text-sm opacity-80">{msg.type?.toUpperCase()} - Every {msg.beat_interval_hours}h</p>
                  </div>
                  <div className={`text-right ${isSending ? 'animate-pulse' : ''}`}>
                    <p className={`text-2xl font-bold ${isUrgent ? 'text-yellow-300' : ''}`}>
                      {timeText}
                    </p>
                    <p className="text-xs opacity-70">until send</p>
                  </div>
                  <button
                    onClick={() => punchSingleMutation.mutate(msg.id)}
                    disabled={punchSingleMutation.isPending}
                    className="ml-3 bg-white/30 hover:bg-white/50 text-white px-3 py-1 rounded text-sm font-medium disabled:opacity-50"
                  >
                    Punch
                  </button>
                </div>
              )
            })}
          </div>
          
          <button
            onClick={() => punchMutation.mutate()}
            disabled={punchMutation.isPending}
            className="bg-white text-orange-600 px-6 py-2 rounded-lg font-bold hover:bg-gray-100 disabled:opacity-50 flex items-center gap-2"
          >
            <Play className="w-5 h-5" />
            {punchMutation.isPending ? 'Punching...' : 
             punchMutation.isSuccess ? 'Punched ✓' : 'Punch In (Reset All)'}
          </button>
        </div>
      )}
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((stat) => {
          const Icon = stat.icon
          return (
            <div key={stat.label} className="card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                  <p className="text-2xl font-bold">{stat.value}</p>
                </div>
              </div>
            </div>
          )
        })}
      </div>
      
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Quick Stats - Your Subscription</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">Current Plan</p>
            <p className="text-lg font-semibold capitalize">{analytics?.subscription?.tier || 'Free'}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">SMS Used</p>
            <p className="text-lg font-semibold">{analytics?.subscription?.sms_used || 0} / {analytics?.subscription?.sms_quota || 5}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">Email Used</p>
            <p className="text-lg font-semibold">{analytics?.subscription?.email_used || 0}</p>
          </div>
        </div>
      </div>
      
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Recent Pending Messages</h2>
        {recentMessages && recentMessages.length > 0 ? (
          <div className="space-y-3">
            {recentMessages.slice(0, 5).map((msg: any) => (
              <div key={msg.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">{msg.subject || msg.body?.substring(0, 50)}</p>
                  <p className="text-sm text-gray-500 capitalize">{msg.type} - {msg.schedule_type}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">
                    {msg.scheduled_at ? format(new Date(msg.scheduled_at), 'MMM d, h:mm a') : 'Beat'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No pending messages</p>
        )}
      </div>
    </div>
  )
}